import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, Upload, FileText, Trash, Lightning, X, DotsThree } from "@phosphor-icons/react";

const FORMATS = [
  { k: "libre", label: "Libre" },
  { k: "courriel", label: "Courriel" },
  { k: "plan_action", label: "Plan d'action" },
  { k: "post_social", label: "Post social" },
  { k: "script_video", label: "Script vidéo" },
  { k: "relance_client", label: "Relance client" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [input, setInput] = useState("");
  const [format, setFormat] = useState("libre");
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const chatRef = useRef(null);

  const loadAll = () => Promise.all([
    api.get(`/projects/${id}`).then(r => setProject(r.data)),
    api.get(`/projects/${id}/messages`).then(r => setMessages(r.data)),
    api.get(`/projects/${id}/documents`).then(r => setDocs(r.data)),
  ]).catch(() => { toast.error("Dossier introuvable"); nav("/app/dossiers"); });
  useEffect(() => { loadAll(); }, [id]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const text = input;
    setInput("");
    setMessages((m) => [...m, { id: "tmp", role: "user", text, created_at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/chat`, { message: text, output_format: format });
      setMessages((m) => [...m.filter(x => x.id !== "tmp"), data.user_message, data.assistant_message]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setMessages((m) => m.filter(x => x.id !== "tmp"));
    } finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/projects/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} ajouté`);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
    e.target.value = "";
  };

  const delDoc = async (docId) => { await api.delete(`/documents/${docId}`); loadAll(); };

  const clearChat = async () => {
    if (!confirm("Effacer l'historique de ce dossier ?")) return;
    await api.delete(`/projects/${id}/messages`);
    setMessages([]); toast.success("Historique effacé");
  };

  if (!project) return <div className="p-12 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div className="flex flex-col h-screen" data-testid="project-detail">
      <header className="hard-border-b px-8 lg:px-12 py-5 flex items-center justify-between">
        <div className="min-w-0">
          <Link to="/app/dossiers" className="ticker text-muted-foreground hover:text-foreground flex items-center gap-2 mb-1" data-testid="back-projects"><ArrowLeft size={12}/> Dossiers</Link>
          <div className="flex items-center gap-3">
            <h1 className="font-display font-black text-2xl tracking-tight truncate">{project.name}</h1>
            <span className="ticker text-muted-foreground">{project.theme}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>setShowDocs(!showDocs)} data-testid="toggle-docs" className="hard-border px-3 py-2 ticker hover:bg-secondary flex items-center gap-2 transition-colors">
            <FileText size={14}/> Documents ({docs.length})
          </button>
          <button onClick={clearChat} data-testid="clear-chat" className="hard-border px-3 py-2 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors">
            <Trash size={14}/>
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Chat */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={chatRef} className="flex-1 overflow-y-auto px-8 lg:px-12 py-6 space-y-5" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="max-w-2xl mx-auto text-center pt-12">
                <Lightning size={42} weight="duotone" className="mx-auto text-[#D90429] mb-4"/>
                <div className="font-display font-bold text-2xl">Comment puis-je t'aider ?</div>
                <div className="text-muted-foreground mt-2">J'ai accès à ton profil, aux instructions de ce dossier et à ses documents. Demande-moi n'importe quoi.</div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
                  {[
                    "Analyse ma situation et donne-moi les 3 actions prioritaires cette semaine.",
                    "Rédige un courriel de relance pour un client qui n'a pas répondu depuis 2 semaines.",
                    "Génère un plan d'action pour atteindre mon objectif principal.",
                    "Propose 5 idées de publications pour ce mois-ci.",
                  ].map((s, i) => (
                    <button key={i} onClick={()=>setInput(s)} className="hard-border p-4 text-sm hover:bg-secondary/40 transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${m.role === "user" ? "bg-foreground text-white p-4" : "hard-border bg-white p-5"}`}>
                  <div className="ticker text-[10px] opacity-60 mb-1">{m.role === "user" ? "Toi" : "Assistant"}</div>
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed font-[Satoshi]">{m.text}</pre>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="hard-border bg-white p-4 text-sm text-muted-foreground">Réflexion en cours…</div>
              </div>
            )}
          </div>

          <div className="hard-border-t px-8 lg:px-12 py-4 bg-white">
            <div className="flex gap-2 mb-3 overflow-x-auto">
              {FORMATS.map(f => (
                <button key={f.k} onClick={()=>setFormat(f.k)} data-testid={`fmt-${f.k}`}
                  className={`ticker hard-border px-3 py-1 whitespace-nowrap transition-colors ${format===f.k?"bg-foreground text-white border-foreground":"hover:bg-secondary"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e)=>setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écris ton message…  (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"
                rows={2}
                className="flex-1 hard-border px-4 py-3 resize-none focus:border-foreground outline-none transition-colors"
              />
              <button onClick={send} disabled={busy || !input.trim()} data-testid="chat-send" className="bg-[#D90429] hover:bg-[#BF0424] text-white px-6 transition-colors disabled:opacity-40 flex items-center gap-2">
                <PaperPlaneTilt size={18} weight="bold"/>
              </button>
            </div>
          </div>
        </div>

        {/* Docs sidebar */}
        {showDocs && (
          <aside className="w-80 hard-border-l flex flex-col bg-secondary/20">
            <div className="hard-border-b px-4 py-3 flex items-center justify-between">
              <div className="ticker">Documents</div>
              <button onClick={()=>setShowDocs(false)} className="text-muted-foreground hover:text-foreground"><X size={16}/></button>
            </div>
            <label className="hard-border-b m-4 p-6 text-center cursor-pointer hover:bg-white transition-colors bg-white" data-testid="upload-label">
              <Upload size={22} className="mx-auto mb-2 text-[#D90429]"/>
              <div className="ticker">Téléverser un fichier</div>
              <div className="text-xs text-muted-foreground mt-1">PDF, TXT · max 5 Mo</div>
              <input type="file" accept=".pdf,.txt,.md,.csv,text/*,application/pdf" onChange={upload} className="hidden" data-testid="upload-input"/>
            </label>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid="docs-list">
              {docs.length === 0 && <div className="text-xs text-muted-foreground">Aucun document.</div>}
              {docs.map(d => (
                <div key={d.id} className="hard-border p-3 bg-white flex items-center gap-3">
                  <FileText size={18} className="text-[#D90429] flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{d.filename}</div>
                    <div className="ticker text-muted-foreground">{(d.size/1024).toFixed(0)} Ko · {(d.content_text||"").length} car.</div>
                  </div>
                  <button onClick={()=>delDoc(d.id)} className="text-muted-foreground hover:text-[#D90429]" data-testid={`doc-del-${d.id}`}><Trash size={14}/></button>
                </div>
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
